import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogoWordmark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  ScanLine, FolderLock, FileSignature, Landmark, Scale,
  LayoutDashboard, LogOut, Crown, UserCog,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/app", label: "Accueil", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/scanner", label: "Scanner", icon: ScanLine, testid: "nav-scanner" },
  { to: "/app/coffre-fort", label: "Coffre-fort", icon: FolderLock, testid: "nav-vault" },
  { to: "/app/courriers", label: "Courriers", icon: FileSignature, testid: "nav-letters" },
  { to: "/app/impots", label: "Impôts", icon: Landmark, testid: "nav-impots" },
  { to: "/app/droits", label: "Vos droits", icon: Scale, testid: "nav-rights" },
];

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (to) =>
    to === "/app" ? location.pathname === "/app" : location.pathname.startsWith(to);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="mx-auto max-w-6xl px-4 h-16 flex items-center justify-between">
          <Link to="/app" data-testid="header-logo-link">
            <LogoWordmark />
          </Link>
          <div className="flex items-center gap-2">
            {!user?.is_premium && (
              <Button
                size="sm"
                variant="outline"
                className="rounded-full border-brand text-brand hover:bg-brand hover:text-white gap-1.5"
                onClick={() => navigate("/app/abonnement")}
                data-testid="header-upgrade-btn"
              >
                <Crown className="h-4 w-4" /> Premium
              </Button>
            )}
            {user?.is_premium && (
              <span
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-success bg-success/10 px-3 py-1.5 rounded-full"
                data-testid="premium-badge"
              >
                <Crown className="h-3.5 w-3.5" /> Premium
              </span>
            )}
            <Button
              size="icon" variant="ghost" className="rounded-full"
              onClick={() => navigate("/app/compte")} data-testid="nav-account"
              title="Mon compte"
            >
              <UserCog className="h-5 w-5" />
            </Button>
            <Button
              size="icon" variant="ghost" className="rounded-full"
              onClick={logout} data-testid="logout-btn" title="Se déconnecter"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
        {/* Desktop nav row */}
        <nav className="hidden md:block border-t border-border/60">
          <div className="mx-auto max-w-6xl px-4 flex gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to} to={n.to} data-testid={n.testid}
                className={cn(
                  "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors duration-200",
                  isActive(n.to)
                    ? "border-brand text-brand"
                    : "border-transparent text-muted-foreground hover:text-brand"
                )}
              >
                <n.icon className="h-4 w-4" /> {n.label}
              </Link>
            ))}
          </div>
        </nav>
      </header>

      <main className="flex-1 mx-auto w-full max-w-6xl px-4 py-6 pb-24 md:pb-10">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-border">
        <div className="grid grid-cols-6">
          {NAV.map((n) => (
            <Link
              key={n.to} to={n.to} data-testid={`m-${n.testid}`}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors",
                isActive(n.to) ? "text-brand" : "text-muted-foreground"
              )}
            >
              <n.icon className="h-5 w-5" />
              {n.label}
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}
